import api from '../api';

const emailService = {
    // === Account Management ===

    // Get all mail accounts for the user
    getAccounts: () => api.get('/mail/accounts'),

    // Create a new mail account
    createAccount: (data) => api.post('/mail/accounts', data),

    // Attach a new email account (replaces existing)
    attachEmail: (data) => api.post('/mail/attach-email', data),

    // Update a mail account
    updateAccount: (id, data) => api.put(`/mail/accounts/${id}`, data),

    // Delete a mail account
    deleteAccount: (id) => api.delete(`/mail/accounts/${id}`),

    // Test connection to mail servers
    testConnection: (data) => api.post('/mail/accounts/test-connection', data),

    // Trigger sync for an account
    syncAccount: (id) => api.post(`/mail/accounts/${id}/sync`),

    // Set account as default
    setDefault: (id) => api.post(`/mail/accounts/${id}/default`),

    // === Temp Upload ===

    // Upload a file to temporary storage.
    // Returns { data: { temp_upload: { id, original_filename, file_size, mime_type } } }
    uploadTempFile: (file, onUploadProgress, signal) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/temp-upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress,
            signal,
        });
    },

    // === Folder Management ===

    // Get all folders for an account
    getFolders: (accountId) => api.get(`/mail/accounts/${accountId}/folders`),
    createFolder: (accountId, name) => api.post(`/mail/accounts/${accountId}/folders`, { name }),
    deleteFolder: (folderId) => api.delete(`/mail/folders/${folderId}`),

    // Sync a specific folder
    syncFolder: (folderId) => api.post(`/mail/folders/${folderId}/sync`),

    // === Email Operations ===

    // Get emails in a folder
    getEmails: (folderId, params = {}) => api.get(`/mail/folders/${folderId}/emails`, { params }),

    // Get a single email
    getEmail: (id) => api.get(`/mail/emails/${id}`),

    // Compose and send a new email
    compose: (accountId, data) => api.post(`/mail/accounts/${accountId}/compose`, data),

    // Reply to an email
    reply: (emailId, data) => api.post(`/mail/emails/${emailId}/reply`, data),

    // Forward an email
    forward: (emailId, data) => api.post(`/mail/emails/${emailId}/forward`, data),

    // Move email to folder
    moveEmail: (emailId, folderId) => api.post(`/mail/emails/${emailId}/move`, { folder_id: folderId }),
    bulkMoveEmails: (emailIds, folderId) => Promise.all(emailIds.map(id => api.post(`/mail/emails/${id}/move`, { folder_id: folderId }))),

    // Mark email as read/unread
    markRead: (emailId, isRead = true) => api.post(`/mail/emails/${emailId}/read`, { is_read: isRead }),

    // Toggle star on email
    toggleStar: (emailId) => api.post(`/mail/emails/${emailId}/star`),

    // Get all starred emails for an account
    getStarredEmails: (accountId, params = {}) =>
        api.get(`/mail/accounts/${accountId}/search`, { params: { is_starred: 1, ...params } }),

    // Archive emails (moves to Archive folder)
    archiveEmails: (accountId, emailIds) => api.post(`/mail/accounts/${accountId}/archive`, { email_ids: emailIds }),

    // Unarchive emails (moves back to Inbox)
    unarchiveEmails: (accountId, emailIds) => api.post(`/mail/accounts/${accountId}/unarchive`, { email_ids: emailIds }),

    // Delete email (move to trash)
    deleteEmail: (emailId) => api.delete(`/mail/emails/${emailId}`),

    // Search emails
    search: (accountId, query, params = {}) =>
        api.get(`/mail/accounts/${accountId}/search`, { params: { q: query, ...params } }),

    // Search emails with AbortController signal (M1: prevents stale results after unmount)
    searchWithSignal: (accountId, query, signal, params = {}) =>
        api.get(`/mail/accounts/${accountId}/search`, { params: { q: query, ...params }, signal }),

    // Update an existing draft (H9)
    updateDraft: (emailId, data) => api.put(`/mail/drafts/${emailId}`, data),

    // Send an existing draft (H9)
    sendDraft: (emailId) => api.post(`/mail/drafts/${emailId}/send`),

    // === Thread Operations ===

    // Get email thread
    getThread: (threadId) => api.get(`/mail/threads/${threadId}`),
};

export default emailService;
